import { supabase } from '@/lib/supabase';
import type { EmployeeRow, ManagerRow, StaffRole } from '@/features/dashboard/data';

/**
 * The admin console's data layer (P4). Deliberately separate from
 * dashboard/data.ts: that file's promise is "the dashboard's reads, RLS-
 * scoped to a manager's own location"; this one is "every row in the four
 * admin areas, RLS-scoped to admins only" — adm_all (migration 0016) is what
 * makes these queries return anything at all for anyone who isn't one.
 *
 * One exception since migration 0020, called out here because the paragraph
 * above no longer covers the whole file: loadRosterData() at the bottom
 * serves the MANAGER-facing Roster page and is scoped by `mgr_read`, not by
 * adm_all. So "admins only" describes loadAdminData(), not this module.
 *
 * Unlike the dashboard, nothing here filters on `active`. An admin needs to
 * see (and un-deactivate) inactive employees and locations, not just the
 * live roster.
 */

export type { EmployeeRow, ManagerRow, StaffRole };

export interface AdminLocationRow {
  id: string;
  name: string;
  active: boolean;
  notes: string | null;
  point_thresholds: number[] | null; // null = inherit company default
  notification_recipients: string[] | null; // null = inherit company default
  pto_allotment: number | null; // null = inherit company default
}

export interface AdminCompanySettingsRow {
  point_thresholds: number[];
  notification_recipients: string[];
  pto_allotment: number | null; // null = no limit
  notify_employee: boolean;
  portal_url: string | null;
  /** Trigger-maintained (migration 0016 §2b); the compare-and-set version for updateCompanySettings(). */
  updated_at: string;
}

export interface AdminData {
  employees: EmployeeRow[];
  managers: ManagerRow[];
  locations: AdminLocationRow[];
  settings: AdminCompanySettingsRow | null;
  healthChecks: HealthCheckFinding[];
}

/** The manager-facing Roster page's reads — the two tables it can actually use. */
export interface RosterData {
  employees: EmployeeRow[];
  locations: AdminLocationRow[];
}

/** P7: one row per offending record from health_check_findings() (migration 0019). */
export type HealthCheckSeverity = 'critical' | 'warning' | 'info';

export interface HealthCheckFinding {
  severity: HealthCheckSeverity;
  check_name: string;
  detail: string;
}

function bail(what: string, error: { message: string }): never {
  throw new Error(`${what}: ${error.message}`);
}

/**
 * P7's Health Checks tab reads through admin_health_check_findings(), not a
 * table — the underlying engine has to see every location regardless of
 * caller, which a plain RLS policy can't express cleanly for a computed,
 * cross-table result. is_admin() is checked INSIDE the RPC; a non-admin gets
 * an empty array back, never a distinguishable error (same convention as
 * every P5 RPC's out-of-scope refusal). Fetched in the same batch as the
 * other three areas, same as them — this console has always loaded
 * everything up front and switched tabs over already-loaded data, not
 * lazily per tab.
 */
export async function loadAdminData(): Promise<AdminData> {
  const [employeesRes, managersRes, locationsRes, settingsRes, healthRes] = await Promise.all([
    supabase.from('employees').select('*').order('full_name'),
    supabase.from('managers').select('*').order('full_name'),
    supabase.from('locations').select('*').order('name'),
    supabase.from('company_settings').select('*'),
    supabase.rpc('admin_health_check_findings'),
  ]);

  if (employeesRes.error) bail('employees', employeesRes.error);
  if (managersRes.error) bail('managers', managersRes.error);
  if (locationsRes.error) bail('locations', locationsRes.error);
  if (settingsRes.error) bail('company_settings', settingsRes.error);
  if (healthRes.error) bail('admin_health_check_findings', healthRes.error);

  return {
    employees: (employeesRes.data ?? []) as EmployeeRow[],
    managers: (managersRes.data ?? []) as ManagerRow[],
    locations: (locationsRes.data ?? []) as AdminLocationRow[],
    // RLS-gated singleton: a non-admin legitimately reads zero rows here too
    // (mgr_read on company_settings only requires is_manager()) — but this
    // file is only ever called from behind the is_admin() gate in
    // AdminConsole.tsx, so an empty result here means something is wrong,
    // not that the viewer lacks settings access.
    settings: ((settingsRes.data ?? [])[0] ?? null) as AdminCompanySettingsRow | null,
    healthChecks: (healthRes.data ?? []) as HealthCheckFinding[],
  };
}

/**
 * The manager-facing Roster page (migration 0020, pre-P8 punch list Tier 4).
 *
 * Deliberately NOT loadAdminData() with the extra areas ignored: a
 * location-scoped manager has no business making the round trips at all.
 * `managers` returns zero rows to them (adm_all is admin-only, so the table
 * stays dark — tests/010 section E pins that), `admin_health_check_findings()`
 * returns an empty array by its own is_admin() gate, and `company_settings`
 * they can read but have nothing here to do with. Asking for four things and
 * using two would make an empty `managers` list look like a load failure the
 * next time someone reads this code.
 *
 * Both reads are RLS-scoped for free: `mgr_read` on employees and locations
 * is `in_scope(location_id)` / `in_scope(id)`, so a manager receives exactly
 * their own location's roster and their own location's row with no client-side
 * filter to keep in sync — the same "let RLS do the scoping" pattern the
 * Dashboard and Manage Records already follow.
 *
 * No `active` filter, same as the admin console: a manager needs to see (and
 * reactivate) someone they previously marked inactive, not just the live
 * roster. RosterTab's own "Show inactive" toggle is what hides them by
 * default.
 */
export async function loadRosterData(): Promise<RosterData> {
  const [employeesRes, locationsRes] = await Promise.all([
    supabase.from('employees').select('*').order('full_name'),
    supabase.from('locations').select('*').order('name'),
  ]);

  if (employeesRes.error) bail('employees', employeesRes.error);
  if (locationsRes.error) bail('locations', locationsRes.error);

  return {
    employees: (employeesRes.data ?? []) as EmployeeRow[],
    locations: (locationsRes.data ?? []) as AdminLocationRow[],
  };
}
